/**
 * Draws a comic page onto a canvas exactly as the editor shows it (minus the
 * editing chrome). This is the single renderer behind PNG export, PDF export,
 * and the page thumbnails, so what you see in the sidebar is what prints.
 *
 * Everything is drawn in page px (96 per inch) under one ctx.scale(), using
 * the same geometry the editor uses: bubble outlines from bubbleShapes, tails
 * from bubbleGeometry, art placement from artPlacement, and panel outlines
 * (including knife-cut polygons) from the cell data. Storyboard pages add
 * frame mattes and safe areas (as FrameMatte) and notes (as ShotNotes).
 */
import { CSS_DPI } from "../constants/pageDimensions";
import { findBubbleShape } from "../constants/bubbleShapes";
import { findComicStyle } from "../constants/comicStyles";
import { findScreentone } from "../constants/screentones";
import { computeTailTriangle, normalizeTails } from "./tailGeometry";
import { tailShape, defaultTailStyle } from "./bubbleGeometry";
import { artPlacement } from "../hooks/useImageSize";
import {
  findBoardLayout, findMatte, matteWindow, safeAreaRects,
  MATTE_BAR_FILL, MATTE_GUIDE_SHADE, MATTE_GUIDE_LINE, SAFE_AREA_LINE,
} from "../constants/storyboard";
import {
  NOTES_FONT, NOTES_INK, NOTES_MUTED, NOTES_RULE, NOTES_HEADER_HEIGHT, NOTES_LABEL_SIZE,
  NOTES_TEXT_SIZE, NOTES_LINE_HEIGHT, notesLayout, shotSummary,
} from "./shotNotes";

const BUBBLE_PADDING = 7;
const BUBBLE_LINE_HEIGHT = 1.4;
const SFX_PADDING = 4;
const SFX_LETTER_SPACING = 2;
const SFX_STROKE = 2;
const SFX_SHADOW = 2;

// ─── Resources ──────────────────────────────────────────────────────────

const imageCache = new Map();
const loadImage = (url) => {
  if (!imageCache.has(url)) {
    imageCache.set(url, new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => {
        imageCache.delete(url);
        reject(new Error("Failed to load image"));
      };
      img.src = url;
    }));
  }
  return imageCache.get(url);
};

/** Make sure every web font the page uses is ready, or canvas silently falls back. */
const ensureFonts = async (cells, style) => {
  if (typeof document === "undefined" || !document.fonts?.load) return;
  const specs = new Set([
    `${style.bubble.fontWeight || "normal"} 16px ${style.bubble.fontFamily}`,
    `bold 16px ${style.sfx.fontFamily}`,
  ]);
  cells.forEach((cell) => (cell.items || []).forEach((item) => {
    if (item.type === "speechBubble") {
      const weight = item.fontWeight || style.bubble.fontWeight || "normal";
      const fontStyle = item.fontStyle || "normal";
      specs.add(`${fontStyle} ${weight} 16px ${item.fontFamily || style.bubble.fontFamily}`);
    } else if (item.type === "sfx") {
      specs.add(`bold 16px ${item.fontFamily || style.sfx.fontFamily}`);
    }
  }));
  await Promise.all([...specs].map((spec) => document.fonts.load(spec).catch(() => null)));
};

// ─── Small helpers ──────────────────────────────────────────────────────

const traceOutline = (ctx, cell) => {
  ctx.beginPath();
  if (cell.polygon) {
    cell.polygon.forEach(([px, py], i) => {
      if (i === 0) ctx.moveTo(cell.x + px, cell.y + py);
      else ctx.lineTo(cell.x + px, cell.y + py);
    });
    ctx.closePath();
  } else {
    ctx.rect(cell.x, cell.y, cell.width, cell.height);
  }
};

// CSS gradient stops like "rgba(0,0,0,0) 58%, rgba(0,0,0,0.07) 100%"
const parseGradientStops = (css) => {
  const stops = [];
  const re = /(rgba?\([^)]*\)|#[0-9a-f]{3,8}|transparent)\s+([\d.]+)%/gi;
  let match;
  while ((match = re.exec(css))) stops.push({ color: match[1], at: Number(match[2]) / 100 });
  return stops;
};

const applyTextTransform = (text, transform) => {
  if (transform === "uppercase") return text.toUpperCase();
  if (transform === "lowercase") return text.toLowerCase();
  if (transform === "capitalize") return text.replace(/\b\w/g, (c) => c.toUpperCase());
  return text;
};

/** Greedy word wrap, like CSS normal wrapping (long words overflow, unbroken). */
const wrapLines = (ctx, text, maxWidth) => {
  const words = text.split(" ").filter(Boolean);
  const lines = [];
  let line = "";
  words.forEach((word) => {
    const candidate = line ? `${line} ${word}` : word;
    if (line && ctx.measureText(candidate).width > maxWidth) {
      lines.push(line);
      line = word;
    } else {
      line = candidate;
    }
  });
  if (line) lines.push(line);
  return lines;
};

const fontMetrics = (ctx, fontSize) => {
  const m = ctx.measureText("Mg");
  const ascent = m.fontBoundingBoxAscent ?? m.actualBoundingBoxAscent ?? fontSize * 0.8;
  const descent = m.fontBoundingBoxDescent ?? m.actualBoundingBoxDescent ?? fontSize * 0.2;
  return { ascent, descent };
};

// Letter-spaced text: native where supported, per-glyph otherwise
const supportsLetterSpacing = (ctx) => "letterSpacing" in ctx;
const spacedWidth = (ctx, text, spacing) => (supportsLetterSpacing(ctx)
  ? ctx.measureText(text).width
  : ctx.measureText(text).width + spacing * text.length);
const drawSpaced = (ctx, text, x, y, spacing, mode) => {
  if (supportsLetterSpacing(ctx)) {
    if (mode === "fill") ctx.fillText(text, x, y);
    else ctx.strokeText(text, x, y);
    return;
  }
  let cursor = x;
  for (const ch of text) {
    if (mode === "fill") ctx.fillText(ch, cursor, y);
    else ctx.strokeText(ch, cursor, y);
    cursor += ctx.measureText(ch).width + spacing;
  }
};

// ─── Page layers ────────────────────────────────────────────────────────

const drawPaper = (ctx, width, height, style) => {
  ctx.fillStyle = style.page.background;
  ctx.fillRect(0, 0, width, height);

  const halftone = style.page.halftone;
  if (halftone && halftone.spacing > 0) {
    ctx.fillStyle = halftone.color;
    ctx.beginPath();
    for (let y = 0; y < height + halftone.spacing; y += halftone.spacing) {
      for (let x = 0; x < width + halftone.spacing; x += halftone.spacing) {
        ctx.moveTo(x + halftone.radius, y);
        ctx.arc(x, y, halftone.radius, 0, Math.PI * 2);
      }
    }
    ctx.fill();
  }

  // "radial-gradient(ellipse at center, …)": farthest-corner ellipse
  if (style.page.vignette) {
    const stops = parseGradientStops(style.page.vignette);
    if (stops.length >= 2) {
      const rx = (width / 2) * Math.SQRT2;
      const ry = (height / 2) * Math.SQRT2;
      ctx.save();
      ctx.translate(width / 2, height / 2);
      ctx.scale(1, ry / rx);
      const gradient = ctx.createRadialGradient(0, 0, 0, 0, 0, rx);
      stops.forEach((stop) => gradient.addColorStop(stop.at, stop.color));
      ctx.fillStyle = gradient;
      ctx.fillRect(-rx, -rx, rx * 2, rx * 2);
      ctx.restore();
    }
  }
};

const drawScreentone = async (ctx, cell, scale) => {
  const pattern = findScreentone(cell.screentone);
  if (!pattern || !pattern.svg) return;
  const size = pattern.patternSize || 8;
  // Rasterise the tile at output resolution so it stays crisp at 300 DPI
  const k = Math.max(1, Math.ceil(scale * 2));
  const color = cell.screentoneColor || "#000000";
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${size * k}" height="${size * k}" viewBox="0 0 ${size} ${size}"><g style="color:${color}" color="${color}">${pattern.svg(size)}</g></svg>`;
  try {
    const tile = await loadImage(`data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`);
    const fill = ctx.createPattern(tile, "repeat");
    if (!fill) return;
    if (fill.setTransform && typeof DOMMatrix !== "undefined") {
      fill.setTransform(new DOMMatrix().translateSelf(cell.x, cell.y).scaleSelf(1 / k));
    }
    ctx.save();
    ctx.globalAlpha = cell.screentoneOpacity ?? 0.3;
    ctx.fillStyle = fill;
    ctx.fillRect(cell.x, cell.y, cell.width, cell.height);
    ctx.restore();
  } catch (e) {
    // A missing tone shouldn't sink the export
  }
};

const drawPanelBorder = (ctx, cell, style) => {
  const bw = cell.borderWidth ?? style.panel.borderWidth;
  if (!(bw > 0)) return;
  const borderStyle = cell.borderStyle || style.panel.borderStyle;
  ctx.save();
  ctx.strokeStyle = cell.borderColor || style.panel.borderColor;
  ctx.lineJoin = "miter";
  // The panel is clipped to its outline, so a double-width stroke leaves
  // exactly bw inside it — like a CSS border on a border-box element
  if (borderStyle === "double" && !cell.polygon) {
    const third = bw / 3;
    ctx.lineWidth = third * 2;
    traceOutline(ctx, cell);
    ctx.stroke();
    ctx.lineWidth = third;
    const inset = third * 2.5;
    ctx.strokeRect(cell.x + inset, cell.y + inset, cell.width - inset * 2, cell.height - inset * 2);
  } else {
    if (borderStyle === "dashed") ctx.setLineDash([bw * 4, bw * 3]);
    else if (borderStyle === "dotted") ctx.setLineDash([bw, bw * 2]);
    ctx.lineWidth = bw * 2;
    traceOutline(ctx, cell);
    ctx.stroke();
  }
  ctx.restore();
};

const drawBubbleShape = (ctx, bubble, style) => {
  const shapeDef = findBubbleShape(bubble.shape);
  const w = bubble.width;
  const h = bubble.height;
  const bgColor = bubble.bgColor || shapeDef.fill || style.bubble.bgColor || "#ffffff";
  const borderColor = bubble.borderColor || style.bubble.borderColor || "#1a1a1a";
  const strokeW = bubble.borderWidth ?? style.bubble.borderWidth ?? 2;

  const body = new Path2D(shapeDef.getPath(w, h));
  ctx.fillStyle = bgColor;
  ctx.fill(body);
  ctx.save();
  ctx.strokeStyle = borderColor;
  ctx.lineWidth = strokeW;
  ctx.lineJoin = "round";
  if (shapeDef.strokeDasharray) ctx.setLineDash(shapeDef.strokeDasharray.split(/[ ,]+/).map(Number));
  ctx.stroke(body);
  ctx.restore();

  if (shapeDef.doubleOutline) {
    ctx.save();
    ctx.translate(3, 3);
    ctx.strokeStyle = borderColor;
    ctx.lineWidth = strokeW;
    ctx.stroke(new Path2D(shapeDef.getPath(w - 6, h - 6)));
    ctx.restore();
  }

  const tailEnabled = bubble.tailEnabled && !shapeDef.noTail;
  if (!tailEnabled) return;
  normalizeTails(bubble).forEach((tail) => {
    const triangle = computeTailTriangle(w, h, tail.tipX, tail.tipY, tail.width || 20);
    if (!triangle) return;
    const shape = tailShape(triangle, tail.style || defaultTailStyle(shapeDef), strokeW);
    if (shape.circles.length) {
      shape.circles.forEach((c) => {
        ctx.beginPath();
        ctx.arc(c.cx, c.cy, c.r, 0, Math.PI * 2);
        ctx.fillStyle = bgColor;
        ctx.fill();
        ctx.strokeStyle = borderColor;
        ctx.lineWidth = 1;
        ctx.stroke();
      });
      return;
    }
    ctx.fillStyle = bgColor;
    ctx.fill(new Path2D(shape.cover));
    ctx.fill(new Path2D(shape.fill));
    ctx.save();
    ctx.strokeStyle = borderColor;
    ctx.lineWidth = strokeW;
    ctx.lineJoin = shape.lineJoin;
    ctx.stroke(new Path2D(shape.stroke));
    ctx.restore();
  });
};

const drawBubbleText = (ctx, bubble, style) => {
  const raw = (bubble.text || "").replace(/\s+/g, " ").trim();
  if (!raw) return;
  const fontSize = bubble.fontSize || 14;
  const fontFamily = bubble.fontFamily || style.bubble.fontFamily || "sans-serif";
  const fontWeight = bubble.fontWeight || style.bubble.fontWeight || "normal";
  const fontStyle = bubble.fontStyle || "normal";
  const textAlign = bubble.textAlign || style.bubble.textAlign || "left";
  const transform = bubble.textTransform || style.bubble.textTransform || "none";
  const text = applyTextTransform(raw, transform);

  ctx.font = `${fontStyle} ${fontWeight} ${fontSize}px ${fontFamily}`;
  ctx.fillStyle = bubble.textColor || style.bubble.textColor || "#1f2937";
  ctx.textBaseline = "alphabetic";
  ctx.textAlign = textAlign === "center" ? "center" : textAlign === "right" ? "right" : "left";

  const innerWidth = bubble.width - BUBBLE_PADDING * 2;
  const lines = wrapLines(ctx, text, innerWidth);
  const lineHeight = fontSize * BUBBLE_LINE_HEIGHT;
  const { ascent, descent } = fontMetrics(ctx, fontSize);
  const halfLeading = (lineHeight - (ascent + descent)) / 2;
  // Vertically centred block, like the editor's flexbox
  const top = (bubble.height - lines.length * lineHeight) / 2;
  const x = textAlign === "center"
    ? bubble.width / 2
    : textAlign === "right" ? bubble.width - BUBBLE_PADDING : BUBBLE_PADDING;
  lines.forEach((line, i) => {
    ctx.fillText(line, x, top + i * lineHeight + halfLeading + ascent);
  });
};

const drawBubble = (ctx, bubble, style) => {
  const placed = {
    shape: "rounded", tailEnabled: true, x: 16, y: 16, width: 160, height: 96, tailWidth: 20,
    ...bubble,
  };
  const opacity = placed.opacity != null ? placed.opacity : 1;
  ctx.save();
  ctx.translate(placed.x, placed.y);
  if (opacity < 1) ctx.globalAlpha = opacity;
  drawBubbleShape(ctx, placed, style);
  drawBubbleText(ctx, placed, style);
  ctx.restore();
};

const drawSfx = (ctx, sfx, style) => {
  const text = sfx.text || "BOOM";
  const fontSize = sfx.fontSize || 64;
  const fontFamily = sfx.fontFamily || style.sfx.fontFamily || "'Bangers', cursive";
  const color = sfx.color || style.sfx.color || "#ff0000";
  const strokeColor = sfx.strokeColor || style.sfx.strokeColor || "#000000";
  const scaleX = sfx.scaleX ?? 1;
  const scaleY = sfx.scaleY ?? 1;

  ctx.save();
  ctx.font = `bold ${fontSize}px ${fontFamily}`;
  if (supportsLetterSpacing(ctx)) ctx.letterSpacing = `${SFX_LETTER_SPACING}px`;
  ctx.textBaseline = "alphabetic";
  ctx.textAlign = "left";
  const { ascent, descent } = fontMetrics(ctx, fontSize);
  const naturalW = spacedWidth(ctx, text, SFX_LETTER_SPACING);
  const naturalH = ascent + descent;
  // The editor's box: padded, scaled text; rotated about its centre
  const boxW = naturalW * scaleX + SFX_PADDING * 2;
  const boxH = naturalH * scaleY + SFX_PADDING * 2;
  ctx.translate((sfx.x || 20) + boxW / 2, (sfx.y || 20) + boxH / 2);
  ctx.rotate(((sfx.rotation || 0) * Math.PI) / 180);
  ctx.translate(-boxW / 2 + SFX_PADDING, -boxH / 2 + SFX_PADDING);
  ctx.scale(scaleX, scaleY);

  ctx.lineJoin = "round";
  // text-shadow: a solid offset copy behind the lettering
  ctx.fillStyle = strokeColor;
  ctx.strokeStyle = strokeColor;
  ctx.lineWidth = SFX_STROKE;
  drawSpaced(ctx, text, SFX_SHADOW, ascent + SFX_SHADOW, SFX_LETTER_SPACING, "fill");
  drawSpaced(ctx, text, SFX_SHADOW, ascent + SFX_SHADOW, SFX_LETTER_SPACING, "stroke");
  // Fill, then -webkit-text-stroke centred on the glyph edges
  ctx.fillStyle = color;
  drawSpaced(ctx, text, 0, ascent, SFX_LETTER_SPACING, "fill");
  drawSpaced(ctx, text, 0, ascent, SFX_LETTER_SPACING, "stroke");
  ctx.restore();
};

/**
 * In the editor a rect panel's contents are positioned inside its CSS border
 * (the padding box); angled panels have no CSS border. Same here.
 */
export const panelInset = (cell, style) =>
  (cell.polygon ? 0 : cell.borderWidth ?? style.panel.borderWidth ?? 0);

// ─── Storyboard layers ──────────────────────────────────────────────────

/** Letterbox bars / crop guide and safe areas over a frame's window. */
const drawFrameMatte = (ctx, width, height, storyboard) => {
  const matte = findMatte(storyboard.matteKey);
  const win = matte.ratio ? matteWindow(width, height, matte.ratio) : null;
  if (win) {
    ctx.save();
    ctx.beginPath();
    ctx.rect(0, 0, width, height);
    ctx.rect(win.x, win.y, win.width, win.height);
    ctx.fillStyle = matte.mode === "bars" ? MATTE_BAR_FILL : MATTE_GUIDE_SHADE;
    ctx.fill("evenodd");
    if (matte.mode === "guide") {
      ctx.strokeStyle = MATTE_GUIDE_LINE;
      ctx.lineWidth = 1;
      ctx.setLineDash([5, 3]);
      ctx.strokeRect(win.x + 0.5, win.y + 0.5, win.width - 1, win.height - 1);
    }
    ctx.restore();
  }
  if (storyboard.safeAreas) {
    const ox = win?.x ?? 0;
    const oy = win?.y ?? 0;
    ctx.save();
    ctx.strokeStyle = SAFE_AREA_LINE;
    ctx.lineWidth = 0.75;
    safeAreaRects(win?.width ?? width, win?.height ?? height).forEach((r) => {
      ctx.setLineDash(r.key === "title" ? [2, 2] : [6, 3]);
      ctx.strokeRect(ox + r.x, oy + r.y, r.width, r.height);
    });
    ctx.restore();
  }
};

/** Paragraph-preserving wrap (textareas keep the user's line breaks). */
const wrapParagraphs = (ctx, text, maxWidth) =>
  text.split("\n").flatMap((para) => {
    const lines = wrapLines(ctx, para.replace(/\s+/g, " ").trim(), maxWidth);
    return lines.length ? lines : [""];
  });

const drawShotNotes = (ctx, cell, shotNumber, compact) => {
  const box = cell.notesBox;
  const shot = cell.shot || {};
  const { sections } = notesLayout(box, { compact });
  ctx.save();
  ctx.translate(box.x, box.y);
  ctx.strokeStyle = NOTES_RULE;
  ctx.lineWidth = 1;
  ctx.strokeRect(0.5, 0.5, box.width - 1, box.height - 1);
  ctx.beginPath();
  ctx.moveTo(0, NOTES_HEADER_HEIGHT - 0.5);
  ctx.lineTo(box.width, NOTES_HEADER_HEIGHT - 0.5);
  ctx.stroke();

  ctx.beginPath();
  ctx.rect(0, 0, box.width, box.height);
  ctx.clip();

  // Header: shot number left, shot type · move · duration right
  ctx.textBaseline = "middle";
  ctx.font = `bold 9.5px ${NOTES_FONT}`;
  ctx.fillStyle = NOTES_INK;
  ctx.textAlign = "left";
  if (supportsLetterSpacing(ctx)) ctx.letterSpacing = "0.6px";
  ctx.fillText(`SHOT ${shotNumber}`, 5, NOTES_HEADER_HEIGHT / 2);
  if (supportsLetterSpacing(ctx)) ctx.letterSpacing = "0px";
  const summary = shotSummary(shot);
  if (summary) {
    ctx.textAlign = "right";
    ctx.fillText(summary, box.width - 5, NOTES_HEADER_HEIGHT / 2);
  }

  sections.forEach((section) => {
    if (section.showLabel) {
      ctx.font = `${NOTES_LABEL_SIZE}px ${NOTES_FONT}`;
      ctx.fillStyle = NOTES_MUTED;
      ctx.textAlign = "left";
      ctx.textBaseline = "top";
      if (supportsLetterSpacing(ctx)) ctx.letterSpacing = "0.8px";
      ctx.fillText(section.label.toUpperCase(), section.x, section.y + 2);
      if (supportsLetterSpacing(ctx)) ctx.letterSpacing = "0px";
    }
    const text = shot[section.field];
    if (!text) return;
    ctx.save();
    ctx.beginPath();
    ctx.rect(section.x, section.textY, section.width, section.textHeight);
    ctx.clip();
    ctx.font = `${NOTES_TEXT_SIZE}px ${NOTES_FONT}`;
    ctx.fillStyle = NOTES_INK;
    ctx.textAlign = "left";
    ctx.textBaseline = "alphabetic";
    const lineHeight = NOTES_TEXT_SIZE * NOTES_LINE_HEIGHT;
    const { ascent, descent } = fontMetrics(ctx, NOTES_TEXT_SIZE);
    const halfLeading = (lineHeight - (ascent + descent)) / 2;
    wrapParagraphs(ctx, text, section.width).forEach((line, i) => {
      ctx.fillText(line, section.x, section.textY + i * lineHeight + halfLeading + ascent);
    });
    ctx.restore();
  });
  ctx.restore();
};

const drawPanel = async (ctx, cell, style, scale, storyboard) => {
  const items = cell.items || [];
  const art = items.find((item) => item.type === "background");
  const inset = panelInset(cell, style);
  const innerW = Math.max(1, cell.width - inset * 2);
  const innerH = Math.max(1, cell.height - inset * 2);

  ctx.save();
  // Everything in a panel is clipped to it, like overflow: hidden on screen
  traceOutline(ctx, cell);
  ctx.clip();

  ctx.fillStyle = art ? "#ffffff" : style.panel.emptyFill;
  ctx.fillRect(cell.x, cell.y, cell.width, cell.height);

  if (art?.url) {
    try {
      const img = await loadImage(art.url);
      const place = artPlacement(
        innerW, innerH,
        { width: img.naturalWidth, height: img.naturalHeight },
        art.zoom || 100, art.panX || 0, art.panY || 0
      );
      ctx.drawImage(img, cell.x + inset + place.left, cell.y + inset + place.top, place.width, place.height);
    } catch (e) {
      console.warn("Couldn't draw panel art:", e);
    }
  }

  if (cell.screentone && cell.screentone !== "none") await drawScreentone(ctx, cell, scale);

  // Bubbles and SFX in their layer order
  ctx.save();
  ctx.translate(cell.x + inset, cell.y + inset);
  items.forEach((item) => {
    if (item.type === "speechBubble") drawBubble(ctx, item, style);
    else if (item.type === "sfx") drawSfx(ctx, item, style);
  });
  ctx.restore();

  if (storyboard) {
    ctx.save();
    ctx.translate(cell.x + inset, cell.y + inset);
    drawFrameMatte(ctx, innerW, innerH, storyboard);
    ctx.restore();
  }

  drawPanelBorder(ctx, cell, style);
  ctx.restore();
};

/**
 * Render a page to a new canvas.
 * @param {Array} cells
 * @param {{pageWidthPx:number, pageHeightPx:number, dpi?:number, scale?:number, comicStyleKey?:string,
 *   storyboard?:object, shotOffset?:number}} options
 *   `scale` (canvas px per page px) wins over `dpi` when both are given.
 *   `storyboard` (a board page's settings) draws mattes and shot notes;
 *   `shotOffset` is the number of shots on earlier pages.
 */
export const renderPageToCanvas = async (cells, options = {}) => {
  const { pageWidthPx = 816, pageHeightPx = 1056, dpi = 150, comicStyleKey, storyboard = null, shotOffset = 0 } = options;
  const scale = options.scale ?? dpi / CSS_DPI;
  const style = findComicStyle(comicStyleKey);

  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(pageWidthPx * scale));
  canvas.height = Math.max(1, Math.round(pageHeightPx * scale));
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Could not create canvas context");

  await ensureFonts(cells, style);
  ctx.scale(scale, scale);
  drawPaper(ctx, pageWidthPx, pageHeightPx, style);

  const ordered = [...cells].sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0));
  for (const cell of ordered) {
    // Panels draw in order; later panels never overlap earlier ones except
    // halves of an angled cut, whose clips keep them apart
    // eslint-disable-next-line no-await-in-loop
    await drawPanel(ctx, cell, style, scale, storyboard);
  }

  if (storyboard) {
    const compact = Boolean(findBoardLayout(storyboard.layoutKey).compact);
    cells.forEach((cell, index) => {
      if (cell.notesBox) drawShotNotes(ctx, cell, shotOffset + index + 1, compact);
    });
  }
  return canvas;
};
